import React, { useState } from "react";
import { useAuth } from "../../../context/authContext";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import PageTitle from "../../../components/PageTitle/PageTitle";
import ExpandableDataGrid from "../../../components/ExpandableDataGrid/ExpandableDataGrid";
import { AddCircleOutline } from "@mui/icons-material";
import "../../Services/Services.css";
import { useTranslation } from "react-i18next";
import NotAuthorized from "../../../components/NotAuthorized/NotAuthorized";
import CreateCaseForm from "../../../components/ContactCCRForm/CreateCaseForm";
import { formatDate } from "../../../utilities/globalServiceUtils";
import { useCasesData } from "../../../hooks/useServiceRecords";
import DataGridSkeleton from "../../../components/PageSkeletons/DataGridSkeleton";

const Cases = () => {
    const { hasPermission } = useAuth();
    const { t } = useTranslation();
    const [createCaseOpen, setCreateCaseOpen] = useState(false);
    const { cases, casesLoading } = useCasesData();


    const columns_cases = [
        { field: "case_number", headerName: t('TABLE_HEADERS.CASE_NUMBER'), width: 150 },
        { field: "case_title", headerName: t('TABLE_HEADERS.TITLE'), flex: 1.5 },
        { field: "case_type", headerName: t('TABLE_HEADERS.TYPE'), width: 150, filterable: true },
        { field: "case_status", headerName: t('TABLE_HEADERS.STATUS'), width: 140, filterable: true },
        { field: "case_priority", headerName: t('TABLE_HEADERS.PRIORITY'), width: 140, filterable: true },
        {
            field: "created_date", headerName: t('TABLE_HEADERS.CREATED_DATE'), width: 200, valueFormatter: (value) => formatDate(value),
            // Ensure proper date sorting even if value is a string
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1).getTime() : 0;
                const d2 = v2 ? new Date(v2).getTime() : 0;
                return d1 - d2;
            },
        },
    ];

    const handleCreateCase = () => {
        setCreateCaseOpen(true);
    };

    const customButtonsCases = [
        {
            label: t('LABELS.CREATE_NEW_CASE'),
            onClick: () => {
                handleCreateCase();
            },
            icon: <AddCircleOutline />,
        },
    ];

    // Removed Create New Workorder button

    const canSeeCases = !!hasPermission('service_management.view_cases');
    const canCreateCase = !!hasPermission('service_management.create_cases');

    // 🚫 If user can't see either tab, block access entirely
    if (!canSeeCases) {
        return <NotAuthorized />;
    }

    if (casesLoading) {
        return (
            <DataGridSkeleton
                containerClassName="services-container"
                actionButtons={canCreateCase ? 1 : 0}
                columns={5}
                rows={6}
            />
        );
    }




    // Ensure valid tab index when permissions change dynamically

    const renderCaseMasterDetails = (caseRow, detailActions) => (
        <Box sx={{
            p: 2, width: '100%', border: "1px solid #e6e8eb",
            borderRadius: 2,
        }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={"bold"}>Case Details: {caseRow?.case_number}</Typography>
                <Typography variant="body2" color="text.secondary">Please review the case details below</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CASE_NUMBER")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_number}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.TYPE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_type}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.STATUS")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_status}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.PRIORITY")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_priority}</Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.TITLE")}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{caseRow?.case_title}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.DESCRIPTIONS")}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_description || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DETAILS")}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{caseRow?.case_resolution_details || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_NOTES", { defaultValue: "Resolution Notes" })}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{caseRow?.case_resolution_notes || "-"}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", columnGap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.CREATED_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.created_date)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{t("TABLE_HEADERS.RESOLUTION_DATE")}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{formatDate(caseRow?.resolution_date) || "-"}</Typography>
                </Box>
            </Box>
            <Divider sx={{ my: 2 }} flexItem />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={detailActions?.collapseRow}>
                    {t("BUTTON.CLOSE")}
                </Button>
            </Box>
        </Box>
    );

    return (
        <div className="services-container">
            <PageTitle title={t('TITLES.CASES_TICKETS')} />


            {cases.length === 0 && !casesLoading ? (
                <Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
                    {t('ALERT_MESSAGES.NO_CASES_FOUND')}
                </Alert>
            ) : (
                <ExpandableDataGrid
                    key="casesGrid"
                    data={cases}
                    columns={columns_cases}
                    pageSize={5}
                    showSearchBar={true}
                    checkboxSelection={false}
                    customButtons={canCreateCase ? customButtonsCases : []}
                    rowIdField="id"
                    sortBy={[{ field: "created_date", sort: "desc" }]}
                    masterDetails={renderCaseMasterDetails}
                    allowMultipleExpand={false}
                />
            )}


            {/* Create Case Modal */}
            <CreateCaseForm open={createCaseOpen} setOpen={setCreateCaseOpen} />


        </div>
    );

};

export default Cases;
